import { ReactElement } from 'react';
import type { FeedbackReport } from '../../api/feedback';

/**
 * The read-only half of a full report: what the user wrote, their
 * screenshots and the auto-captured context. Shared by the triage modal
 * and the expanding ticket card so the rendering conventions (plain text
 * only, presigned image links) live in one place.
 */
export default function FeedbackReportBody({ report }: { report: FeedbackReport }): ReactElement {
  return (
    <>
      {/* User text is rendered as plain text, never as HTML. */}
      {report.body && (
        <pre
          className="cr-m-0 cr-whitespace-pre-wrap cr-break-words cr-rounded-md cr-bg-gray-100 cr-p-3
            cr-font-label cr-text-sm cr-text-blue-900"
        >
          {report.body}
        </pre>
      )}

      {!!report.attachments?.length && (
        <div className="cr-flex cr-flex-wrap cr-gap-2">
          {report.attachments.map((attachment) => (
            <a
              key={attachment.id}
              href={attachment.download_url ?? '#'}
              target="_blank"
              rel="noreferrer"
              title={attachment.file_name ?? attachment.s3_key}
            >
              <img
                src={attachment.download_url ?? ''}
                alt={attachment.file_name ?? 'screenshot'}
                className="cr-h-28 cr-rounded cr-border cr-border-gray-350 cr-object-cover"
              />
            </a>
          ))}
        </div>
      )}

      {report.context && (
        <details>
          <summary className="cr-cursor-pointer cr-text-sm cr-font-semibold cr-text-blue-900">
            Captured context
          </summary>
          <pre className="cr-mt-2 cr-max-h-64 cr-overflow-auto cr-rounded-md cr-bg-gray-100 cr-p-3 cr-text-xs">
            {JSON.stringify(report.context, null, 2)}
          </pre>
        </details>
      )}
    </>
  );
}
